// src/lib/costFunding.ts
//
// BekalNanti — Milestone P4: Education Cost + Funding
// Pure Data Types, Constants, and Deterministic Readiness Logic.
// ZERO AI, ZERO external APIs, ZERO assumptions/predictions.

export type CostCategory =
  | 'tuition'
  | 'living'
  | 'travel'
  | 'admin_visa'
  | 'equipment'
  | 'insurance'
  | 'other'

export type CostFrequency =
  | 'one_off'
  | 'per_year'
  | 'per_semester'
  | 'per_month'
  | 'total_program'

export type CertaintyStatus = 'known' | 'estimated' | 'unknown'

export interface GoalCostItem {
  id?: string
  goalId: string
  category: CostCategory
  name: string
  amount: number | null // null when certainty === 'unknown'
  currency: string
  frequency: CostFrequency
  certainty: CertaintyStatus
  notes?: string | null
  createdAt?: string
  updatedAt?: string
}

export type FundingSourceType =
  | 'existing_savings'
  | 'scholarship'
  | 'family_contribution'
  | 'employer_sponsorship'
  | 'government_support'
  | 'education_loan'
  | 'other'

export type FundingStatus = 'planned' | 'applied' | 'confirmed'

export interface GoalFundingSource {
  id?: string
  goalId: string
  sourceType: FundingSourceType
  name: string
  amount: number | null // null when certainty === 'unknown'
  currency: string
  frequency?: string | null
  status: FundingStatus
  isConfirmed: boolean
  sourceReference?: string | null
  certainty: CertaintyStatus
  notes?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface CostCategoryOption {
  id: CostCategory
  label: string
  description: string
  defaultFrequency: CostFrequency
}

export const COST_CATEGORIES: CostCategoryOption[] = [
  {
    id: 'tuition',
    label: 'Uang Kuliah & Biaya Pendidikan',
    description: 'SPP berkala, uang pangkal / UKT, biaya SKS, atau biaya pendaftaran',
    defaultFrequency: 'per_year',
  },
  {
    id: 'living',
    label: 'Tempat Tinggal & Biaya Hidup',
    description: 'Sewa asrama/kost, makan harian, dan kebutuhan hidup esensial',
    defaultFrequency: 'per_year',
  },
  {
    id: 'travel',
    label: 'Transportasi & Perjalanan',
    description: 'Tiket keberangkatan/kepulangan dan transportasi lokal harian',
    defaultFrequency: 'one_off',
  },
  {
    id: 'admin_visa',
    label: 'Visa, Dokumen & Administrasi',
    description: 'Pengurusan visa pelajar, tes bahasa (IELTS/TOEFL), paspor, dan legalisasi dokumen',
    defaultFrequency: 'one_off',
  },
  {
    id: 'equipment',
    label: 'Buku, Laptop & Perlengkapan',
    description: 'Buku teks, perangkat belajar, seragam laboratorium, atau alat praktikum',
    defaultFrequency: 'one_off',
  },
  {
    id: 'insurance',
    label: 'Asuransi & Syarat Kesehatan',
    description: 'Asuransi kesehatan wajib (misal OSHC), imunisasi, atau tes medis wajib',
    defaultFrequency: 'per_year',
  },
  {
    id: 'other',
    label: 'Biaya Terkait Lainnya',
    description: 'Pengeluaran terencana lainnya yang spesifik untuk sasaran ini',
    defaultFrequency: 'one_off',
  },
]

export interface FundingTypeOption {
  id: FundingSourceType
  label: string
  description: string
}

export const FUNDING_TYPES: FundingTypeOption[] = [
  {
    id: 'existing_savings',
    label: 'Tabungan Pribadi Dialokasikan',
    description: 'Simpanan atau aset lancar yang dialokasikan khusus untuk tujuan ini',
  },
  {
    id: 'scholarship',
    label: 'Beasiswa / Bantuan Finansial',
    description: 'Beasiswa penuh/parsial (LPDP, universitas, yayasan, atau pemerintah)',
  },
  {
    id: 'family_contribution',
    label: 'Dukungan / Hibah Keluarga',
    description: 'Bantuan dari orang tua, kakek-nenek, atau keluarga besar',
  },
  {
    id: 'employer_sponsorship',
    label: 'Sponsor Tempat Kerja / Ikatan Dinas',
    description: 'Dukungan biaya studi dari perusahaan atau institusi kerja',
  },
  {
    id: 'government_support',
    label: 'Subsidi / Bantuan Pemerintah',
    description: 'Subsidi biaya studi, KIP Kuliah, atau program bantuan pemerintah',
  },
  {
    id: 'education_loan',
    label: 'Pinjaman Pendidikan (Student Loan)',
    description: 'Fasilitas pinjaman berbunga rendah untuk pembiayaan kuliah',
  },
  {
    id: 'other',
    label: 'Sumber Pendanaan Lainnya',
    description: 'Pendapatan magang, kerja sampingan kampus, atau sumber lainnya',
  },
]

export const FREQUENCY_LABELS: Record<CostFrequency, string> = {
  one_off: 'Satu Kali (One-off)',
  per_year: 'Per Tahun',
  per_semester: 'Per Semester',
  per_month: 'Per Bulan',
  total_program: 'Total Keseluruhan Program',
}

export const FUNDING_STATUS_LABELS: Record<FundingStatus, string> = {
  planned: 'Direncanakan',
  applied: 'Sedang Diajukan',
  confirmed: 'Sudah Dikonfirmasi / Diterima',
}

export const CERTAINTY_LABELS: Record<CertaintyStatus, string> = {
  known: 'Pasti (Diketahui)',
  estimated: 'Estimasi (Perkiraan)',
  unknown: 'Belum Diketahui',
}

export interface P4MappingSummary {
  totalCostsMapped: number
  costItemsCount: number
  knownCostsCount: number
  estimatedCostsCount: number
  unknownCostsCount: number
  totalFundingMapped: number
  fundingSourcesCount: number
  knownFundingCount: number
  estimatedFundingCount: number
  unknownFundingCount: number
}

/**
 * Compute descriptive summary of mapped cost items and funding sources.
 * IMPORTANT: This is an input mapping summary, NOT an affordability or gap calculation.
 */
export function summarizeCostAndFunding(
  costItems: GoalCostItem[],
  fundingSources: GoalFundingSource[]
): P4MappingSummary {
  let totalCosts = 0
  let knownCosts = 0
  let estimatedCosts = 0
  let unknownCosts = 0

  for (const item of costItems) {
    if (item.certainty === 'unknown' || item.amount === null) {
      unknownCosts++
    } else {
      totalCosts += Number(item.amount || 0)
      if (item.certainty === 'known') knownCosts++
      else estimatedCosts++
    }
  }

  let totalFunding = 0
  let knownFunding = 0
  let estimatedFunding = 0
  let unknownFunding = 0

  for (const src of fundingSources) {
    if (src.certainty === 'unknown' || src.amount === null) {
      unknownFunding++
    } else {
      totalFunding += Number(src.amount || 0)
      if (src.certainty === 'known') knownFunding++
      else estimatedFunding++
    }
  }

  return {
    totalCostsMapped: totalCosts,
    costItemsCount: costItems.length,
    knownCostsCount: knownCosts,
    estimatedCostsCount: estimatedCosts,
    unknownCostsCount: unknownCosts,
    totalFundingMapped: totalFunding,
    fundingSourcesCount: fundingSources.length,
    knownFundingCount: knownFunding,
    estimatedFundingCount: estimatedFunding,
    unknownFundingCount: unknownFunding,
  }
}

export type P4ReadinessStatus = 'not_started' | 'in_progress' | 'ready_for_calculation'

export interface P4ReadinessResult {
  status: P4ReadinessStatus
  isReadyForCalculation: boolean
  hasCosts: boolean
  hasFunding: boolean
  mappedCostCategoriesCount: number
  missingRequirements: string[]
}

/**
 * Pure Deterministic P4 Readiness Evaluator.
 * Evaluates whether a target has sufficient structured cost mapping
 * to proceed to P5 Deterministic Calculation Engine.
 *
 * Rules:
 * - NOT_STARTED: No cost items and no funding sources mapped.
 * - IN_PROGRESS: Some costs mapped, but missing tuition/primary education component or zero items.
 * - READY_FOR_CALCULATION: At least 1 valid cost item exists (tuition or general costs),
 *   allowing P5 to normalize and project the future requirements.
 */
export function evaluateP4Readiness(
  costItems: GoalCostItem[],
  fundingSources: GoalFundingSource[],
  goalType: string = 'education'
): P4ReadinessResult {
  const hasCosts = costItems.length > 0
  const hasFunding = fundingSources.length > 0

  const isEducation = !goalType || goalType === 'education'

  if (!hasCosts && !hasFunding) {
    return {
      status: 'not_started',
      isReadyForCalculation: false,
      hasCosts: false,
      hasFunding: false,
      mappedCostCategoriesCount: 0,
      missingRequirements: [
        isEducation
          ? 'Belum ada komponen biaya pendidikan yang dipetakan'
          : 'Belum ada komponen biaya yang dipetakan',
      ],
    }
  }

  const distinctCategories = new Set(costItems.map((c) => c.category))
  const missingRequirements: string[] = []

  if (!hasCosts) {
    missingRequirements.push(
      isEducation
        ? 'Belum ada komponen biaya pendidikan yang dicatat'
        : 'Belum ada komponen biaya yang dicatat'
    )
  }

  // A goal is ready for calculation if it has at least 1 cost item mapped (even if some are unknown)
  const isReady = hasCosts && costItems.length >= 1

  return {
    status: isReady ? 'ready_for_calculation' : 'in_progress',
    isReadyForCalculation: isReady,
    hasCosts,
    hasFunding,
    mappedCostCategoriesCount: distinctCategories.size,
    missingRequirements,
  }
}
